/**
 * ColumnsPopover — checklist popover for showing/hiding columns.
 *
 * Triggered by a "Columns" button in the filter band. Toggles per-column
 * visibility; `alwaysVisible` columns render disabled (can't be hidden).
 * State is per-view (Team / Users) and lives in TeamManagementView.
 */
import React from 'react';
import { Box, Checkbox, ChevronDownIcon, HStack, Popover, Pressable, VStack } from 'native-base';
import { Text } from '@pimm/base';
import type { ColumnDef } from './team-management-columns';

type Props = {
  columns: ColumnDef[];
  visibleIds: string[];
  onChange: (nextIds: string[]) => void;
  buttonLabel: string;
  popoverTitle: string;
  resolveLabel: (key: string) => string;
  // Which source is the FOREIGN one for this view — those are the columns
  // that get a tag in the popover. Team view: tag = 'user' (User-sourced
  // columns flagged because Team is mostly Employee). Users view: tag =
  // 'employee' (Employee-sourced columns flagged because Users is mostly
  // User). The tag label changes to match ("User" / "Employee").
  tagSource: 'user' | 'employee';
};

export const ColumnsPopover: React.FC<Props> = ({ columns, visibleIds, onChange, buttonLabel, popoverTitle, resolveLabel, tagSource }) => {
  // Columns whose source matches `tagSource` get the small inline tag — they're
  // the "foreign" record on the current view. Color is per-source so the two
  // views can be told apart at a glance:
  //   - User tag (Team view)      → blue    (login data is the foreigner)
  //   - Employee tag (Users view) → green   (employee data is the foreigner)
  const tagLabel = resolveLabel(
    tagSource === 'user' ? 'store_settings:columns_popover_tag_user' : 'store_settings:columns_popover_tag_employee',
  );
  const tagBg = tagSource === 'user' ? 'blue.100' : 'success.100';
  const tagText = tagSource === 'user' ? 'blue.700' : 'success.700';
  const shouldTag = (col: ColumnDef) => (col.source ?? 'employee') === tagSource;
  const toggle = (id: string, alwaysVisible: boolean) => {
    if (alwaysVisible) return;
    const isOn = visibleIds.includes(id);
    onChange(isOn ? visibleIds.filter(x => x !== id) : [...visibleIds, id]);
  };

  return (
    <Popover
      trigger={triggerProps => (
        <Pressable {...triggerProps} _hover={{ bg: 'gray.50' }}>
          <HStack
            alignItems="center"
            justifyContent="center"
            space={1.5}
            px={3}
            h={9}
            bg="white"
            borderWidth={1}
            borderColor="gray.300"
            rounded="lg"
            shadow={1}
          >
            <Text size="sm" fontWeight={500} color="gray.700">
              {buttonLabel}
            </Text>
            <ChevronDownIcon size={3} color="gray.500" />
          </HStack>
        </Pressable>
      )}
      placement="bottom right"
    >
      <Popover.Content w={260} borderWidth={0} shadow="7">
        <Popover.Body p={0} bg="white">
          <VStack py={1}>
            <HStack px={4} py={1.5}>
              <Text size="sm" fontWeight={600} color="gray.500" textTransform="uppercase">
                {popoverTitle}
              </Text>
            </HStack>
            {columns.map(col => {
              const isOn = !!col.alwaysVisible || visibleIds.includes(col.id);
              return (
                <Pressable
                  key={col.id}
                  onPress={() => toggle(col.id, !!col.alwaysVisible)}
                  _hover={col.alwaysVisible ? undefined : { bg: 'gray.50' }}
                  opacity={col.alwaysVisible ? 0.55 : 1}
                >
                  <HStack px={4} minH={9} alignItems="center" space={2}>
                    <Checkbox value={col.id} isChecked={isOn} isReadOnly accessibilityLabel={resolveLabel(col.labelKey)} />
                    <Text size="md" fontWeight={600} color="gray.800" flex={1}>
                      {resolveLabel(col.labelKey)}
                    </Text>
                    {shouldTag(col) && (
                      <Box bg={tagBg} rounded="sm" px={1.5} py={0.5}>
                        <Text size="xs" fontWeight={600} color={tagText}>
                          {tagLabel}
                        </Text>
                      </Box>
                    )}
                  </HStack>
                </Pressable>
              );
            })}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
